import React from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useAuthState } from "../../Context";

const WebinarScheduleCard = ({ items }) => {
  const { t } = useTranslation();

  const { user } = useAuthState();

  const checkWebinarLive = (scheduledAt, duration) => {
    const scheduledDate = new Date(scheduledAt);
    const currentDate = new Date();

    const durationInMilliseconds = Number(duration) * 60000;
    const endTime = new Date(scheduledDate.getTime() + durationInMilliseconds);
    const isSameDate =
      scheduledDate.toDateString() === currentDate.toDateString();
    const isWithinInterval =
      currentDate >= scheduledDate && currentDate <= endTime;

    return isSameDate && isWithinInterval;
  };

  const calculateDaysBefore = (scheduledAt) => {
    const now = new Date();
    const eventDate = new Date(scheduledAt);

    const isSameDay =
      now.getFullYear() === eventDate.getFullYear() &&
      now.getMonth() === eventDate.getMonth() &&
      now.getDate() === eventDate.getDate();

    if (isSameDay) {
      const timeDifference = eventDate - now;
      const hoursDifference = timeDifference / (1000 * 60 * 60);

      if (hoursDifference < 1) {
        return `${t("startingSoon")}`;
      } else {
        const roundedHoursDifference = Math.round(hoursDifference);
        return `${t("hoursCountdown", {
          hoursDifference: roundedHoursDifference,
        })}`;
      }
    }

    const timeDifference = eventDate - now;
    const daysDifference = Math.ceil(timeDifference / (1000 * 60 * 60 * 24));

    return `${t("dayCountdown", { daysDifference })}`;
  };

  const calculateProgressBarWidth = (scheduledAt, isPaidWebinar = false) => {
    const currentTime = new Date().getTime();
    const scheduledTime = new Date(scheduledAt).getTime();
    const remainingHours = Math.max(
      0,
      Math.ceil((scheduledTime - currentTime) / (1000 * 60 * 60)),
    );

    const totalHours = isPaidWebinar ? 168 : 72;

    let progressBarWidth = Math.min(
      100,
      Math.max(0, ((totalHours - remainingHours) / totalHours) * 100),
    );

    if (progressBarWidth === 0) {
      progressBarWidth = 5;
    }

    return progressBarWidth;
  };

  const calculateProgressBarLiveWidth = (scheduledAt, duration) => {
    const now = new Date();
    const startTime = new Date(scheduledAt);
    const endTime = new Date(startTime.getTime() + duration * 60 * 1000);
    const timeLeft = endTime - now;

    if (timeLeft <= 0) {
      return "100%";
    }

    const totalDuration = duration * 60 * 1000;
    const elapsedDuration = totalDuration - timeLeft;
    const percentageElapsed = (elapsedDuration / totalDuration) * 100;

    return percentageElapsed;
  };

  return (
    <div className="-mx-px grid grid-cols-1 sm:grid-cols-2 sm:mx-0 lg:grid-cols-3 gap-4">
      {items.map((item) => {
        const isWebinarLive = checkWebinarLive(item.scheduledAt, item.duration);
        const isPaid = item.type === "PAID";

        const discountPercentage =
          item.normalPrice && item.price
            ? Math.round(
                ((item.normalPrice - item.price) / item.normalPrice) * 100,
              )
            : 0;
        return (
          <a
            key={item.id}
            href={`/webinar/${item.id}`}
            className="flex border border-gray-200 rounded-lg p-4"
          >
            <div className="flex justify-between flex-col flex-auto gap-4 overflow-hidden">
              <div className="flex flex-col gap-3">
                {item.imageUrl ? (
                  <div className="relative rounded-md w-full h-auto aspect-video overflow-hidden">
                    <img
                      className="absolute object-contain w-full h-full z-10"
                      src={item.imageUrl}
                      alt="Banner"
                      loading="lazy"
                    />
                    <div
                      className="absolute w-full h-full bg-fill blur-[50px] brightness-90 mask-gradient"
                      style={{
                        backgroundImage: `url('${item.imageUrl}')`,
                      }}
                    ></div>
                  </div>
                ) : (
                  <div className="rounded-md w-full h-auto aspect-video text-center overflow-hidden bg-gray-200 text-sm text-gray-500 flex flex-col justify-center">
                    {t("No Image")}
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-bold text-gray-900 line-clamp-2">
                    {item.title}
                  </h3>
                </div>
              </div>
              {isPaid && (
                <div className="flex flex-col gap-1 border rounded-md border-gray-300 p-2 bg-slate-50">
                  <div className="flex items-center gap-2 flex-wrap">
                    <NumericFormat
                      displayType="text"
                      value={item.price}
                      thousandSeparator="."
                      decimalSeparator=","
                      prefix="Rp"
                      className="text-sm font-bold text-gray-900"
                    />
                    {item.normalPrice && (
                      <>
                        <NumericFormat
                          displayType="text"
                          value={item.normalPrice}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="Rp"
                          className="text-xs font-normal text-gray-400 line-through"
                        />
                        {discountPercentage > 0 && (
                          <span className="text-xs font-semibold text-red-600">
                            -{discountPercentage}%
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {user?.membership === "PREMIUM" && (
                    <div className="inline-flex items-center w-fit">
                      <span className="px-2 py-1 text-xs font-semibold text-blue-600 bg-blue-100 rounded">
                        {t("webinars.membersOnly")}
                      </span>
                    </div>
                  )}
                </div>
              )}
              {isWebinarLive ? (
                <div className="bg-blue-400 rounded-xl shadow-sm overflow-hidden">
                  <div className="relative p-0.5 flex items-center justify-center">
                    <div
                      className="absolute top-0 bottom-0 left-0 bg-blue-600"
                      style={{
                        width: `${calculateProgressBarLiveWidth(
                          item.scheduledAt,
                          item.duration,
                        )}%`,
                      }}
                    ></div>
                    <div className="relative text-white text-center font-medium text-xs">
                      {t("liveNow")}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-red-400 rounded-xl shadow-sm overflow-hidden">
                  <div className="relative p-0.5 flex items-center justify-center">
                    <div
                      className="absolute top-0 bottom-0 left-0 bg-red-600"
                      style={{
                        width: `${calculateProgressBarWidth(
                          item.scheduledAt,
                          isPaid,
                        )}%`,
                      }}
                    ></div>
                    <div className="relative text-white text-center font-medium text-xs">
                      {calculateDaysBefore(item.scheduledAt)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </a>
        );
      })}
    </div>
  );
};

export default WebinarScheduleCard;
